
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Film, LogOut } from "lucide-react";
import { useAuth } from '@/contexts/AuthContext';
import { useProjects } from '@/hooks/useProjects';
import ProjectGrid from '@/components/dashboard/ProjectGrid';
import CreateProjectModal from '@/components/dashboard/CreateProjectModal';
import ProjectFilters from '@/components/dashboard/ProjectFilters';

const Dashboard = () => {
  const { user, signOut } = useAuth();
  const { data: projects, isLoading } = useProjects();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'draft' | 'completed' | 'exported'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleSignOut = async () => {
    await signOut();
  };

  // Filter projects based on search and tab
  const filteredProjects = projects?.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         project.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    switch (activeTab) {
      case 'draft':
        return matchesSearch && project.export_status === 'none';
      case 'completed':
        return matchesSearch && project.export_status === 'completed';
      case 'exported':
        return matchesSearch && project.exported_video_storage_path;
      default:
        return matchesSearch;
    }
  }) || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-sm bg-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Film className="h-8 w-8 text-purple-400 mr-3" />
              <h1 className="text-2xl font-bold text-white">wedemo</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search projects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
              </div>
              
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Project
              </Button>
              
              <Button
                variant="outline"
                onClick={handleSignOut}
                className="border-white/20 text-white hover:bg-white/10"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">
            Welcome back, {user?.email?.split('@')[0]}
          </h2>
          <p className="text-gray-300">
            Manage your video projects and create stunning content with AI assistance.
          </p>
        </div>

        {/* Project Filters */}
        <ProjectFilters
          activeTab={activeTab}
          onTabChange={setActiveTab}
          projectCount={filteredProjects.length}
        />

        {/* Project Grid */}
        <ProjectGrid
          projects={filteredProjects}
          isLoading={isLoading}
          onCreateProject={() => setShowCreateModal(true)}
        />

        {/* Create Project Modal */}
        <CreateProjectModal
          open={showCreateModal}
          onOpenChange={setShowCreateModal}
        />
      </main>
    </div>
  );
};

export default Dashboard;
